<template>
    <v-client-table :columns="options.columns" :data="data" :options="options" :show-sort-icons="true" ref="table">

        <!-- Header -->
        <div class="intro" slot="beforeFilter" style="position: relative;" v-if="header">
            <h2>{{ $t('contracts.tabs.custom-contracts') }}</h2>
            <p v-if="getTenantAccess !== null">{{ $t('custom-contracts.overview.description') }}</p>
            <p v-else>{{ $t('custom-contracts.overview.description-tenant') }}</p>
        </div>
        <!-- / Header -->

        <!-- Filter zone -->

        <div v-if="controls === true" slot="afterFilter" class="add-button" >
            <contract-filter v-model="orderBy"/>
            <input-search v-model="query"/>

            <router-link disabled to="#" class="button green">
                <SvgIcon icon="plus-circle"/>
                {{ $t('label.button.upload') }}
            </router-link>
        </div>

        <!-- / Filter zone -->


        <!-- Rows -->
        <div slot="address"
             class="data-label"
             :data-label="$t('properties.table.screenings.address')"
             slot-scope="props">
            <a @click.prevent="open(props.row)"  class="row-link">
                <span>{{ props.row.property.address | extract_street }}</span>
            </a>
        </div>

        <div slot="city" class="data-label" slot-scope="props">
            {{ props.row.property.zip_code }} {{ props.row.property.city }}
        </div>

        <div slot="tenant" class="data-label" slot-scope="props">
            <span v-if="props.row.tenants.length > 0">{{ props.row.tenants | tenantName }}</span>
            <span v-else>{{ $t('general.not-defined.label') }}</span>
        </div>

        <div slot="landlord" class="data-label" slot-scope="props">
            <span v-if="props.row.landlord">{{ props.row.landlord.name }}</span>
            <span v-else>{{ props.row.landlord_email }}</span>
        </div>

        <div slot="status" class="data-label" slot-scope="props">
            {{ $t( getContractStatusText(props.row) ) }}
        </div>
        <!-- / Rows -->

        <!-- More Menu -->
        <a v-if="controls === true" slot="more" slot-scope="props">
            <table-dropdown>
                <li @click="open(props.row)">
                    {{ $t('button.contract.edit') }}
                </li>
                <li v-on:click="deleteContract(props.row.id)">
                    {{ $t('general.delete.button') }}
                </li>
            </table-dropdown>
        </a>
        <!-- / More Menu -->

    </v-client-table>
</template>

<script>
import TableDropdown from "../../components/TableDropdown";
import SvgIcon from "../../components/Svg";
import createEntity from "../../mixins/create-entity";
import customContractMixin, {
    APPROVED,
    ROLE_AGENT,
    ROLE_APPROVING_LANDLORD,
    ROLE_LANDLORD,
    ROLE_TENANT,
    SENT_TO_LANDLORD,
    SENT_TO_TENANT,
    SIGNED_BY_LANDLORD,
    SIGNED_BY_TENANT
} from '../../mixins/custom-contract';

import {mapGetters} from "vuex";
import InputSearch from "../../components/InputSearch";
import ContractFilter from "../../components/ContractFilter";

export default {
    name: "CustomContractList",
    mixins: [customContractMixin, createEntity],
    props: {
        data: Array,
        controls: {
            type: Boolean,
            default: true
        },
        header: {
            type: Boolean,
            default: true
        }
    },
    components: {
        ContractFilter,
        InputSearch,
        TableDropdown,
        SvgIcon
    },
    data: () => ({
        orderBy: 'id,desc',
        query: ''
    }),
    filters: {
        extract_street: function (value) {

            let regexp = /\,\s?[0-9]+ ([^0-9]+)$/g;

            return value.replace(regexp, '');
        },

        tenantName: function (tenants) {
            let name = tenants[0].full_name || tenants[0].email;

            if(tenants.length > 1)
                name += ' + ' + (tenants.length-1);

            return name;
        }
    },
    computed: {
        options() {
            let columns = ['address', 'city', 'tenant', 'landlord', 'status'];

            let headings = {
                address: 'Address',
                city: 'City',
                tenant: 'Tenant',
                landlord: 'Landlord',
                status: 'Status',
            };

            if (this.controls) {
                columns.push('more');
                headings.more = 'More';
            }

            return {
                columns: columns,
                headings: headings,
                sortable: [],
                filterable: [],
                texts: {
                    filterPlaceholder: 'Search',
                    filter: '',
                    noResults: ''
                },
                columnsClasses: {
                    address: 'address',
                    city: 'city',
                    tenant: 'tenant',
                    landlord: 'landlord',
                    status: 'status',
                    more: 'more'
                }
            }
        },
        ...mapGetters(['isTenant', 'isLandlord', 'myId', 'getEmail', 'getTenantAccess'])
    },
    methods: {
        open(customContract) {
            let status = this.getContractStatusSet(customContract);

            if (status.includes(SIGNED_BY_TENANT) && status.includes(SIGNED_BY_LANDLORD)) {
                // Completed

                this.$router.push({
                    name: 'custom-contract::view',
                    params: {id: customContract.id}
                });

                return;
            }

            else if (status.includes(ROLE_TENANT)) {
                // Viewer is a tenant

                this.$router.push({
                    name: 'custom-contract::view',
                    params: {id: customContract.id}
                });

            }

            else if (status.includes(ROLE_AGENT)) {
                // Viewer is broker for this contract

                if (!status.includes(SENT_TO_LANDLORD)) {
                    this.$router.push({
                        name: 'custom-contract::edit',
                        params: {
                            id: customContract.id,
                        }
                    });

                    return;
                }

                if (status.includes(SENT_TO_LANDLORD) && !status.includes(APPROVED)) {
                    this.$router.push({
                        name: 'custom-contract::status',
                        params: {id: customContract.id}
                    });

                    return;
                }

                if (status.includes(SENT_TO_LANDLORD) && status.includes(APPROVED) && !status.includes(SIGNED_BY_TENANT)) {
                    this.$router.push({
                        name: 'custom-contract::status',
                        params: {id: customContract.id}
                    });

                    return;
                }

                if (status.includes(SENT_TO_TENANT) && !status.includes(SIGNED_BY_TENANT)) {
                    this.$router.push({
                        name: 'custom-contract::status',
                        params: {id: customContract.id}
                    });

                    return;
                }

                if (status.includes(SENT_TO_TENANT) && status.includes(SIGNED_BY_TENANT)) {
                    this.$router.push({
                        name: 'custom-contract::view',
                        params: {id: customContract.id}
                    });

                    return;
                }
            }

            else if (status.includes(ROLE_LANDLORD)) {

                // Viewer is a landlord

                if (!status.includes(SENT_TO_TENANT)) {

                    // Not sent to tenant

                    this.$router.push({
                        name: 'custom-contract::edit',
                        params: {
                            id: customContract.id,
                        }
                    });

                    return;
                }

                if (status.includes(SENT_TO_TENANT) && !status.includes(SIGNED_BY_TENANT)) {

                    // Sent to tenant but unsigned

                    this.$router.push({
                        name: 'custom-contract::status',
                        params: {
                            id: customContract.id
                        }
                    });

                    return;
                }

                if (status.includes(SENT_TO_TENANT) && status.includes(SIGNED_BY_TENANT)) {

                    // Sent to tenant and signed

                    this.$router.push({
                        name: 'custom-contract::view',
                        params: {id: customContract.id}
                    });

                    return;
                }

            }

            else if (status.includes(ROLE_APPROVING_LANDLORD)) {

                this.$router.push({
                    name: 'custom-contract::view',
                    params: {id: customContract.id}
                });

                return;

            } else {

                this.$router.push({
                    name: 'custom-contract::edit',
                    params: {
                        id: customContract.id,
                    }
                });

                return;
            }

        },
        deleteContract(id) {
            if (confirm(this.$t('contracts.delete.confirmation'))) {
                axios({
                    method: 'DELETE',
                    url: `${window.config.apiUrl}api/custom-contract/${id}`,
                    cancelToken: window.AuthorizationCancelSource.token,
                }).then(response => {
                    this.$store.commit('setSuccessBarMessage', this.$t('digital-contract.contract-deleted.label'));
                    this.$emit('deleted');
                });
            }
        },
    },
    created() {
        this.options.headings.address = this.$t('properties.table.contracts.label');
        this.options.headings.city = this.$t('properties.table.contracts.city');
        this.options.headings.tenant = this.$t('properties.table.contracts.tenant');
        this.options.headings.landlord = this.$t('tenant-contracts.landlord.label');
        this.options.headings.status = this.$t('properties.table.contracts.status');
        this.options.headings.more = this.$t('tables.more.label');
        this.options.texts.filterPlaceholder = this.$t('search.filterplaceholder');
        this.options.texts.noResults = this.$t('table.no-results.label');
    },
    watch: {
        orderBy: function () {
            this.$emit('filter', this.query, this.orderBy)
        },
        query: function () {
            this.$emit('filter', this.query, this.orderBy)
        }
    }
}
</script>

<style scoped>

</style>
